import { useLayoutEffect, useRef } from "react";

/**
 * Shrinks (never grows) this element's own font-size, in small steps, until
 * its rendered content stops overflowing its box — so a longer translation
 * degrades to a smaller size *inside the exact same box* instead of wrapping
 * onto extra lines, spilling past its width, or pushing surrounding layout.
 * English (or any string that already fits at its CSS-authored size) is
 * left completely untouched, since the loop below is a no-op when nothing
 * overflows.
 *
 * This does not change container width/height, breakpoints, or any other
 * element — it only ever adjusts this element's own `font-size`.
 *
 * Props:
 *  - `as`: element/tag to render (default "div") — pass the same tag the
 *    caller would otherwise use (h1, h2, span, button label wrapper, ...).
 *  - `maxLines`: how many lines the *English* content occupies. Pass this
 *    for headings that are meant to hold a fixed number of lines (e.g. the
 *    hero's 2-line headline) so a translation that would wrap onto extra
 *    lines shrinks back down to fit that same line count. Omit it to only
 *    guard against horizontal/single-line overflow (buttons, badges, nav
 *    labels, table cells).
 *  - `minScale`: floor as a fraction of the natural size (default 0.75, i.e.
 *    never shrinks past 75%) so text always stays readable.
 * All other props (className, style, data-testid, ...) pass through to the
 * rendered element unchanged.
 */
export default function FitText({ as: Tag = "div", minScale = 0.75, maxLines, children, ...rest }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const fit = () => {
      // Reset to the CSS-authored (e.g. Tailwind responsive text-*) size
      // before re-measuring — this is what makes switching to a shorter
      // language, or a wider viewport, restore full size instead of
      // staying shrunk from a previous measurement.
      el.style.fontSize = "";
      const naturalPx = parseFloat(window.getComputedStyle(el).fontSize);
      if (!naturalPx) return;

      const fits = () => {
        if (el.scrollWidth > el.clientWidth + 1) return false;
        if (maxLines) {
          const lineHeightPx = parseFloat(window.getComputedStyle(el).lineHeight);
          if (lineHeightPx && el.scrollHeight > lineHeightPx * maxLines + 1) return false;
        }
        return true;
      };

      let px = naturalPx;
      const minPx = naturalPx * minScale;
      const step = Math.max(0.5, naturalPx * 0.02);
      while (!fits() && px > minPx) {
        px -= step;
        el.style.fontSize = `${px}px`;
      }
    };

    fit();

    // Observe the *parent*, not this element — observing the element being
    // resized by our own `fit()` call would retrigger itself immediately.
    //
    // The parent's HEIGHT isn't actually independent of our own font-size the
    // way the above assumes: with `maxLines`, this element sits in an
    // auto-height container (e.g. a grid cell with no fixed height), so
    // `fit()` resetting to natural size and re-measuring genuinely changes the
    // parent's box on every call — which the observer reports right back to
    // us, re-triggering `fit()` inside the delivery of its own callback. The
    // browser's ResizeObserver loop-guard catches exactly that pattern and
    // fires "ResizeObserver loop completed with undelivered notifications",
    // which webpack-dev-server's overlay then shows as a fatal error even
    // though nothing crashed.
    //
    // `fit()` only ever needs to react to available WIDTH changing (a real
    // breakpoint, a sidebar opening, the viewport resizing) — height changes
    // are a side effect of our own mutation, never a cause to re-fit. Gating
    // on width breaks the feedback loop at its source instead of papering
    // over the symptom with a debounce.
    // `fits()` below reads back scrollWidth/scrollHeight/clientWidth right
    // after `fit()` mutates font-size — each read forces a synchronous layout
    // recalculation, and the while loop does this up to ~12 times per call.
    // Running that whole reflow-heavy sequence *inside* the ResizeObserver's
    // own callback stack is what the browser's re-entrant delivery algorithm
    // catches, even from a single legitimate trigger — the width-gate above
    // stops us from calling `fit()` an extra time, but can't stop THIS call's
    // internal reflow storm from being flagged while still nested inside the
    // observer's delivery. Deferring to the next frame moves the work outside
    // that stack entirely, which is the standard fix for this warning.
    let ro;
    let lastWidth = el.parentElement?.getBoundingClientRect().width;
    let raf = 0;
    if (el.parentElement && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width;
        if (width === undefined || width === lastWidth) return;
        lastWidth = width;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(fit);
      });
      ro.observe(el.parentElement);
    }
    return () => {
      cancelAnimationFrame(raf);
      ro && ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, minScale, maxLines]);

  return (
    <Tag ref={ref} {...rest}>
      {children}
    </Tag>
  );
}
